import { el, on } from '../util/dom';
import { DeviceSettings, SettingsFormState, TIMEZONE_OPTIONS, getTimezoneDisplayName, getTimezoneList } from './types';
import { SETTINGS_FORM_STYLES } from './styles';

export interface SettingsFormProps {
  settings: DeviceSettings;
  onSave: (settings: SettingsFormState) => Promise<void>;
  onReset: () => Promise<void>;
  onCancel: () => void;
}

export function createSettingsForm(props: SettingsFormProps): HTMLElement {
  const container = el('div', { class: 'settings-form-container' });

  // Add styles
  injectSettingsFormStyles();

  // Form
  const form = el('form', { class: 'settings-form', novalidate: '' });

  // Device name
  const nameSection = createFormSection(
    'Device Name',
    'Display name for this device (e.g., "Timmy\'s iPad")',
    createTextInput('displayName', props.settings.displayName, 'Device name')
  );
  form.appendChild(nameSection);

  // Restriction mode
  const modeSection = createFormSection(
    'Restriction Mode',
    'BLOCKLIST: block specific apps (default allow). ALLOWLIST: allow specific apps (default block).',
    createRadioGroup('restrictionMode', props.settings.restrictionMode, [
      { value: 'BLOCKLIST', label: 'Blocklist (default allow)' },
      { value: 'ALLOWLIST', label: 'Allowlist (default block)' },
    ])
  );
  form.appendChild(modeSection);

  // New app policy
  const policySection = createFormSection(
    'New App Default',
    'What to do when a new app is installed?',
    createRadioGroup('newAppPolicy', props.settings.newAppPolicy, [
      { value: 'ALLOW', label: 'Allow (no restrictions)' },
      { value: 'BLOCK', label: 'Block (no access)' },
      { value: 'LIMIT', label: 'Limit (set daily limit)' },
    ])
  );
  form.appendChild(policySection);

  // New app limit (conditional)
  const limitInput = createTextInput('newAppDefaultLimitMinutes', String(props.settings.newAppDefaultLimitMinutes || 0), 'Daily limit (minutes)');
  limitInput.type = 'number';
  limitInput.min = '0';
  limitInput.step = '5';
  const limitSection = createFormSection(
    'New App Daily Limit (minutes)',
    'When new app policy is "Limit", how many minutes per day?',
    limitInput
  );
  form.appendChild(limitSection);

  // Timezone
  const timezoneSelect = el('select', {
    class: 'form-input form-select',
    name: 'timezone',
  }) as HTMLSelectElement;

  getTimezoneList().forEach(tz => {
    const option = el('option', {
      value: tz.value,
      selected: tz.value === props.settings.timezone ? 'selected' : undefined,
    }, tz.display) as HTMLOptionElement;
    timezoneSelect.appendChild(option);
  });

  const timezoneSection = createFormSection(
    'Timezone',
    'Used for scheduling and time-based policies',
    timezoneSelect
  );
  form.appendChild(timezoneSection);

  // Notifications
  const notifCheckbox = el('input', {
    type: 'checkbox',
    class: 'form-checkbox',
    name: 'notificationsEnabled',
    checked: props.settings.notificationsEnabled ? 'checked' : undefined,
  }) as HTMLInputElement;

  const notifLabel = el('label', { class: 'form-checkbox-label' });
  notifLabel.appendChild(notifCheckbox);
  notifLabel.appendChild(el('span', {}, 'Enable notifications'));

  const notifSection = createFormSection(
    'Notifications',
    'Receive alerts about blocks, limit reached, and other events',
    notifLabel
  );
  form.appendChild(notifSection);

  // Notification types
  const notifTypes = props.settings.notificationTypes || {
    blockEvents: true,
    limitReached: true,
    appInstalled: false,
    scheduleChange: false,
  };

  const notifTypesContainer = el('div', { class: 'notification-types' });

  const blockEventsCheck = el('label', { class: 'form-checkbox-label' });
  blockEventsCheck.appendChild(
    el('input', {
      type: 'checkbox',
      class: 'form-checkbox',
      name: 'notificationBlockEvents',
      checked: notifTypes.blockEvents ? 'checked' : undefined,
    })
  );
  blockEventsCheck.appendChild(el('span', {}, 'Block events'));
  notifTypesContainer.appendChild(blockEventsCheck);

  const limitReachedCheck = el('label', { class: 'form-checkbox-label' });
  limitReachedCheck.appendChild(
    el('input', {
      type: 'checkbox',
      class: 'form-checkbox',
      name: 'notificationLimitReached',
      checked: notifTypes.limitReached ? 'checked' : undefined,
    })
  );
  limitReachedCheck.appendChild(el('span', {}, 'Limit reached'));
  notifTypesContainer.appendChild(limitReachedCheck);

  const appInstalledCheck = el('label', { class: 'form-checkbox-label' });
  appInstalledCheck.appendChild(
    el('input', {
      type: 'checkbox',
      class: 'form-checkbox',
      name: 'notificationAppInstalled',
      checked: notifTypes.appInstalled ? 'checked' : undefined,
    })
  );
  appInstalledCheck.appendChild(el('span', {}, 'App installed'));
  notifTypesContainer.appendChild(appInstalledCheck);

  const scheduleChangeCheck = el('label', { class: 'form-checkbox-label' });
  scheduleChangeCheck.appendChild(
    el('input', {
      type: 'checkbox',
      class: 'form-checkbox',
      name: 'notificationScheduleChange',
      checked: notifTypes.scheduleChange ? 'checked' : undefined,
    })
  );
  scheduleChangeCheck.appendChild(el('span', {}, 'Schedule change'));
  notifTypesContainer.appendChild(scheduleChangeCheck);

  const notifTypesSection = createFormSection(
    'Notification Types',
    '',
    notifTypesContainer
  );
  form.appendChild(notifTypesSection);

  container.appendChild(form);

  // Actions
  const actions = el('div', { class: 'settings-actions' });

  const cancelBtn = el('button', { class: 'btn btn-secondary', type: 'button' }, 'Cancel');
  on(cancelBtn, 'click', () => props.onCancel());
  actions.appendChild(cancelBtn);

  const resetBtn = el('button', { class: 'btn btn-secondary', type: 'button' }, 'Reset to Defaults');
  on(resetBtn, 'click', async () => {
    if (confirm('Reset all settings to defaults?')) {
      try {
        resetBtn.disabled = true;
        await props.onReset();
      } catch (err) {
        alert(`Failed to reset: ${(err as Error).message}`);
        resetBtn.disabled = false;
      }
    }
  });
  actions.appendChild(resetBtn);

  const saveBtn = el('button', { class: 'btn btn-primary', type: 'submit' }, 'Save Settings') as HTMLButtonElement;
  actions.appendChild(saveBtn);

  container.appendChild(actions);

  // Form submission
  on(form, 'submit', async (e) => {
    e.preventDefault();

    const formData = new FormData(form);
    const settings: SettingsFormState = {
      displayName: (formData.get('displayName') as string) || 'Device',
      restrictionMode: (formData.get('restrictionMode') as any) || 'BLOCKLIST',
      newAppPolicy: (formData.get('newAppPolicy') as any) || 'ALLOW',
      newAppDefaultLimitMinutes: Math.max(0, parseInt(formData.get('newAppDefaultLimitMinutes') as string, 10) || 0),
      timezone: (formData.get('timezone') as string) || 'America/New_York',
      notificationsEnabled: formData.has('notificationsEnabled'),
      notificationBlockEvents: formData.has('notificationBlockEvents'),
      notificationLimitReached: formData.has('notificationLimitReached'),
      notificationAppInstalled: formData.has('notificationAppInstalled'),
      notificationScheduleChange: formData.has('notificationScheduleChange'),
    };

    saveBtn.disabled = true;
    try {
      await props.onSave(settings);
    } catch (err) {
      alert(`Failed to save: ${(err as Error).message}`);
      saveBtn.disabled = false;
    }
  });

  return container;
}

// Helper: Create form section
function createFormSection(title: string, hint: string, input: HTMLElement): HTMLElement {
  const section = el('div', { class: 'form-section' });
  const label = el('label', { class: 'form-label' }, title);
  section.appendChild(label);

  if (hint) {
    const hintEl = el('div', { class: 'form-hint' }, hint);
    section.appendChild(hintEl);
  }

  section.appendChild(input);
  return section;
}

// Helper: Create text input
function createTextInput(name: string, value: string, placeholder: string): HTMLInputElement {
  return el('input', {
    type: 'text',
    class: 'form-input',
    name,
    value,
    placeholder,
    required: 'required',
  }) as HTMLInputElement;
}

// Helper: Create radio group
function createRadioGroup(name: string, selectedValue: string, options: Array<{ value: string; label: string }>): HTMLElement {
  const group = el('div', { class: 'form-radio-group' });

  options.forEach(opt => {
    const label = el('label', { class: 'form-radio-label' });
    label.appendChild(el('input', {
      type: 'radio',
      class: 'form-radio',
      name,
      value: opt.value,
      checked: opt.value === selectedValue ? 'checked' : undefined,
    }));
    label.appendChild(el('span', {}, opt.label));
    group.appendChild(label);
  });

  return group;
}

// Helper: Inject styles
function injectSettingsFormStyles() {
  if (document.getElementById('settings-form-styles')) return;

  const style = el('style');
  style.id = 'settings-form-styles';
  style.textContent = SETTINGS_FORM_STYLES;
  document.head.appendChild(style);
}
